import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/Alert';
import {
  Clock, 
  Search, 
  Filter, 
  CheckCircle, 
  AlertCircle, 
  Eye, 
  Edit, 
  Calendar, 
  Tag, 
  User, 
  MessageSquare, 
  Star, 
  TrendingUp, 
  BookOpen, 
  FileText,
  BarChart3,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import authService from '../../../api/authService';

const KB_ARTICLE_APPROVER_ROLES = new Set([
  'admin',
  'administrator',
  'super_admin',
  'superadmin',
  'team_lead'
]);

const PendingReview = () => {
  const navigate = useNavigate();
  const [pendingArticles, setPendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [reviewingArticle, setReviewingArticle] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);

  const currentUser = authService.getCurrentUser();
  const rawRole = (currentUser?.roleCode || currentUser?.role || sessionStorage.getItem('roleCode') || '').toString();
  const roleNorm = rawRole.toLowerCase().replace(/-/g, '_');
  const canApproveKbArticle = KB_ARTICLE_APPROVER_ROLES.has(roleNorm);

  const mockPendingArticles = [
    {
      id: 'ART-005',
      title: 'Advanced Network Troubleshooting',
      status: 'pending-review',
      priority: 'high',
      category: 'Network Support',
      author: 'Agent Smith',
      submittedDate: '2023-10-22',
      dueDate: '2023-10-25',
      reviewer: 'Senior Agent',
      content: 'Comprehensive guide for advanced network troubleshooting techniques...',
      tags: ['network', 'advanced', 'troubleshooting'],
      lastUpdate: '2 hours ago',
      reviewNotes: 'Please add more examples for common scenarios',
    },
    {
      id: 'ART-006',
      title: 'Database Backup Procedures',
      status: 'pending-review',
      priority: 'medium',
      category: 'Technical Support',
      author: 'Agent Johnson',
      submittedDate: '2023-10-21',
      dueDate: '2023-10-24',
      reviewer: 'Tech Lead',
      content: 'Step-by-step procedures for database backup and recovery...',
      tags: ['database', 'backup', 'procedures'],
      lastUpdate: '4 hours ago',
      reviewNotes: 'Good content, needs formatting improvements',
    },
    {
      id: 'ART-007',
      title: 'Mobile App Configuration',
      status: 'pending-review',
      priority: 'low',
      category: 'Software Support',
      author: 'Agent Brown',
      submittedDate: '2023-10-20',
      dueDate: '2023-10-23',
      reviewer: 'Mobile Expert',
      content: 'Configuration guide for mobile applications...',
      tags: ['mobile', 'app', 'configuration'],
      lastUpdate: '1 day ago',
      reviewNotes: null,
    },
    {
      id: 'ART-008',
      title: 'Security Best Practices',
      status: 'pending-review',
      priority: 'critical',
      category: 'Security Support',
      author: 'Agent Davis',
      submittedDate: '2023-10-19',
      dueDate: '2023-10-22',
      reviewer: 'Security Lead',
      content: 'Essential security best practices for end users...',
      tags: ['security', 'best-practices', 'guidelines'],
      lastUpdate: '2 days ago',
      reviewNotes: 'Critical article - needs immediate review',
    },
  ];

  const categories = ['Network Support', 'Technical Support', 'Software Support', 'Security Support'];

  const filteredArticles = pendingArticles
    .filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .filter(article => filterPriority === 'all' || article.priority === filterPriority)
    .filter(article => filterCategory === 'all' || article.category === filterCategory)
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.submittedDate) - new Date(a.submittedDate);
      }
      if (sortBy === 'oldest') {
        return new Date(a.submittedDate) - new Date(b.submittedDate);
      }
      if (sortBy === 'priority') {
        const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      if (sortBy === 'due-date') {
        return new Date(a.dueDate) - new Date(b.dueDate);
      }
      return 0;
    });

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical': return <Badge variant="destructive">Critical</Badge>;
      case 'high': return <Badge variant="warning">High</Badge>;
      case 'medium': return <Badge variant="info">Medium</Badge>;
      case 'low': return <Badge variant="secondary">Low</Badge>;
      default: return <Badge variant="secondary">{priority}</Badge>;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Network Support': return 'text-blue-600';
      case 'Technical Support': return 'text-green-600';
      case 'Software Support': return 'text-purple-600';
      case 'Security Support': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    // Simulate loading pending articles
    const loadPendingArticles = () => {
      setLoading(true);
      setTimeout(() => {
        setPendingArticles(mockPendingArticles);
        setLoading(false);
      }, 1000);
    };

    loadPendingArticles();
  }, []);

  const handleReviewArticle = (article) => {
    toast.success(`Opening review panel for ${article.id}...`);
    setReviewingArticle(article);
    // In a real app, navigate to detailed review page
    // navigate(`/agent/knowledge/review/${article.id}`);
  };

  const handleEditArticle = (article) => {
    toast.success(`Opening editor for ${article.id}...`);
    setEditingArticle(article);
    // In a real app, navigate to article editor
    // navigate(`/agent/knowledge/edit/${article.id}`);
  };

  const handleApproveArticle = (articleId) => {
    if (!canApproveKbArticle) {
      toast.error('Only an administrator or team lead can approve articles.');
      return;
    }
    const article = pendingArticles.find(a => a.id === articleId);
    setPendingArticles(pendingArticles.filter(article => article.id !== articleId));
    toast.success(`Article ${articleId} "${article?.title}" approved and published!`);
  };

  const handleRejectArticle = (articleId) => {
    const article = pendingArticles.find(a => a.id === articleId);
    setPendingArticles(pendingArticles.filter(article => article.id !== articleId));
    toast.error(`Article ${articleId} "${article?.title}" rejected`);
  };

  const handleRequestRevision = (articleId) => {
    const article = pendingArticles.find(a => a.id === articleId);
    toast.warning(`Revision requested for ${articleId} "${article?.title}"`);
    //console.log('Requesting revision for article:', articleId);
    // In a real app, this would send the article back to the author
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pending articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Pending Review</h1>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Pending Articles</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">{pendingArticles.length}</div>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">Awaiting review</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300">Critical Priority</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-800 dark:text-red-200">{pendingArticles.filter(a => a.priority === 'critical').length}</div>
            <p className="text-xs text-red-600 dark:text-red-400">Urgent review needed</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Due Today</CardTitle>
            <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">{pendingArticles.filter(a => getDaysUntilDue(a.dueDate) <= 0).length}</div>
            <p className="text-xs text-blue-600 dark:text-blue-400">Overdue reviews</p>
          </CardContent>
        </Card>

      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-1/3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            type="text"
            placeholder="Search pending articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-3 py-2 border rounded-md w-full"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="priority">Priority</option>
            <option value="due-date">Due Date</option>
          </select>
        </div>

        {/* <Button variant="outline" className="flex items-center gap-2"> 
          <BarChart3 className="h-4 w-4" /> Review Analytics
        </Button>*/}
      </div>

      {/* Pending Articles List */}
      <div className="space-y-4">
        {filteredArticles.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Pending Articles Found</AlertTitle>
            <AlertDescription>
              No articles match your current search and filter criteria. Try adjusting your filters.
            </AlertDescription>
          </Alert>
        ) : (
          filteredArticles.map(article => {
            const daysUntilDue = getDaysUntilDue(article.dueDate);
            const isOverdue = daysUntilDue <= 0;
            
            return (
              <Card key={article.id} className={`hover:shadow-md transition-shadow ${isOverdue ? 'border-red-200 bg-red-50 dark:bg-red-900/10' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{article.title}</h3>
                        {getPriorityBadge(article.priority)}
                        {isOverdue && (
                          <Badge variant="destructive">Overdue</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">ID:</span> {article.id} | 
                        <span className={`font-medium ml-1 ${getCategoryColor(article.category)}`}> {article.category}</span>
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {article.content}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" /> Author: {article.author}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" /> Submitted: {article.submittedDate}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" /> Due: {article.dueDate} ({daysUntilDue > 0 ? `${daysUntilDue} days` : 'Overdue'})
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" /> Reviewer: {article.reviewer}
                        </div>
                      </div>
                      {article.reviewNotes && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            <span className="font-medium">Review Notes:</span> {article.reviewNotes}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <Tag className="h-4 w-4" /> 
                        {article.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="mr-1">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="mt-4 md:mt-0 flex flex-col items-end space-y-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Last Update: {article.lastUpdate}</span>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleReviewArticle(article)}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Review
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => handleEditArticle(article)}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        {canApproveKbArticle ? (
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => handleApproveArticle(article.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                        ) : (
                          <Button 
                            variant="secondary" 
                            size="sm"
                            disabled
                            className="opacity-50 cursor-not-allowed"
                            title="Only an administrator or team lead can approve"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Pending approval
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRequestRevision(article.id)}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Revise
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleRejectArticle(article.id)}
                        >
                          <AlertCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PendingReview;
